import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import ReviewCard from '../../components/ReviewCard';
import ReviewForm from '../../components/ReviewForm';
import Pagination from '../../components/Pagination';
import { useFavorites } from '../../hooks/useFavourites';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';

export default function ProductPage() {
  const { productId } = useParams();
  const { toggleProductFavorite, isProductFavorite } = useFavorites();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [reviewPagination, setReviewPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    pages: 1
  });
  const [showReviewForm, setShowReviewForm] = useState(false);

  const handleFavoriteToggle = async () => {
    if (!user) {
      toast.info('Please login to add favorites');
      navigate('/login');
      return;
    }

    try {
      await toggleProductFavorite(productId);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const fetchProductReviews = async (page = 1) => {
    try {
      const params = {
        page,
        limit: reviewPagination.limit
      };

      const { data } = await api.get(`/reviews/product/${productId}`, { params });
      setReviews(data.data);
      setReviewPagination({
        ...data.pagination,
        page: parseInt(data.pagination.page, 10)
      });
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await api.get(`/products/${productId}`);
        setProduct(data.data.product);
        // Set first variant as default if available
        if (data.data.product.variants?.length > 0) {
          setSelectedVariant(data.data.product.variants[0]._id);
        }
        // Fetch reviews after product is loaded
        await fetchProductReviews();
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.info('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    try {
      await api.post('/cart', {
        productId,
        variantId: selectedVariant,
        quantity
      });
      toast.success('Item added to cart!');
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error(err.response?.data?.message || 'Failed to add to cart');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (error) return <div className="text-center text-red-500">{error}</div>;
  if (!product) return <div className="text-center text-gray-600">Product not found</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 max-w-6xl mx-auto">
      <button
        onClick={handleFavoriteToggle}
        className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
      >
        {isProductFavorite(productId) ? (
          <HeartSolid className="h-6 w-6 text-pink-600" />
        ) : (
          <HeartOutline className="h-6 w-6 text-gray-400" />
        )}
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Product Image */}
        <div>
          <img
            src={product.images?.[0] || product.design?.image || 'https://via.placeholder.com/400'}
            alt={product.name}
            className="w-full h-auto rounded-lg shadow"
          />
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
          <p className="text-gray-700 mb-4">{product.description}</p>

          {/* Rating */}
          <div className="flex items-center mb-4">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                i < Math.floor(product.stats?.averageRating || 0) ? (
                  <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />
                ) : (
                  <StarIconOutline key={i} className="h-5 w-5 text-gray-300" />
                )
              ))}
            </div>
            <span className="ml-2 text-sm text-gray-600">
              {product.stats?.averageRating?.toFixed(1) || '0.0'} ({product.stats?.totalReviews || 0} reviews)
            </span>
          </div>

          {/* Variant Selection */}
          {product.variants?.length > 1 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Variants</label>
              <div className="flex flex-wrap gap-2">
                {product.variants.map(variant => (
                  <button
                    key={variant._id}
                    onClick={() => setSelectedVariant(variant._id)}
                    className={`px-3 py-1 rounded-md border ${selectedVariant === variant._id 
                      ? 'bg-pink-600 text-white border-pink-600' 
                      : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {variant.color?.name || variant.size || variant.material}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Price */}
          <div className="mb-4">
            <span className="text-2xl font-semibold text-pink-600">
              ₹{selectedVariant 
                ? product.variants.find(v => v._id === selectedVariant)?.finalPrice 
                : product.variants?.[0]?.finalPrice || product.price || 0}
            </span>
            {product.variants?.[0]?.basePrice && (
              <span className="ml-2 text-sm text-gray-500 line-through">
                ₹{product.variants.find(v => v._id === selectedVariant)?.basePrice}
              </span>
            )}
          </div>

          {/* Quantity Selector */}
          <div className="mb-4 flex items-center">
            <button 
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              className="px-3 py-1 border border-gray-300 rounded-l-md"
            >
              -
            </button>
            <span className="px-4 py-1 border-t border-b border-gray-300">
              {quantity}
            </span>
            <button 
              onClick={() => setQuantity(q => q + 1)}
              className="px-3 py-1 border border-gray-300 rounded-r-md"
            >
              +
            </button>
          </div>

          {/* Store Info */}
          <div className="mb-4 flex items-center gap-3">
            {product.store?.logo?.url && (
              <img
                src={product.store.logo.url}
                alt={product.store?.name || "Store logo"}
                className="w-10 h-10 rounded-full border border-gray-300"
              />
            )}
            <p className="text-sm text-gray-500">
              Sold by: <span className="font-medium">{product.store?.name}</span>
            </p>
          </div>

          {/* Add to Cart Button */}
          <button 
            onClick={handleAddToCart}
            className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-md transition w-full md:w-auto mb-8"
            disabled={!selectedVariant}
          >
            Add to Cart
          </button>

          {/* Write Review Button */}
          <button
            onClick={() => setShowReviewForm(true)}
            className="px-4 py-2 border border-pink-600 text-pink-600 rounded-md hover:bg-pink-50 transition w-full md:w-auto"
          >
            Write a Review
          </button>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-12 bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Customer Reviews</h3>
          <div className="flex items-center">
            <button
              onClick={() => setShowReviewForm(true)}
              className="px-3 py-1 text-sm rounded-md text-white"
              style={{ backgroundColor: product.store?.theme?.primaryColor || '#7C3AED' }}
            >
              Write a Review
            </button>
          </div>
        </div>

        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <StarIconOutline className="h-full w-full" />
            </div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No reviews yet</h3>
            <p className="mt-1 text-gray-500">Be the first to review this product!</p>
            <button
              onClick={() => setShowReviewForm(true)}
              className="mt-4 px-4 py-2 rounded-md text-white"
              style={{ backgroundColor: product.store?.theme?.primaryColor || '#7C3AED' }}
            >
              Write a Review
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {reviews.map((review) => (
                <ReviewCard 
                  key={review._id} 
                  review={review} 
                  onProductClick={() => navigate(`/store/${review.store.slug}/products/${review.product._id}`)}
                />
              ))}
            </div>

            {reviewPagination.pages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  currentPage={reviewPagination.page}
                  totalPages={reviewPagination.pages}
                  onPageChange={(newPage) => fetchProductReviews(newPage)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <ReviewForm
          product={product}
          store={product.store}
          onClose={() => setShowReviewForm(false)}
          onSubmit={() => {
            fetchProductReviews();
            setShowReviewForm(false);
          }}
        />
      )}
    </div>
  );
}